// src/services/plaintextGuard.js — the archive forgets itself, the session does not.
//
// 🟥 "THE LAPTOP LEFT AT HOME." Expiring the session at fourteen days was never
//    protecting that machine, because IndexedDB does not care whether anybody is
//    signed in — the decrypted history is readable off the disk either way. What
//    protected it was the WIPE the expiry triggers, and there is no reason the
//    wipe has to wait for the logout. So it does not: the archive is dropped
//    after a day of nobody being there, the identity key and the session stay,
//    and coming back re-syncs from the phone.
//
// 🟥 THE STAMP IS LOCAL, NOT THE ONE IN FIRESTORE. `lastActive` on the session
//    document needs a network round trip to read, and the machine this protects
//    is exactly the one that may be offline, asleep, or on a network that is
//    gone. A guard that cannot answer without the network fails open precisely
//    when it is needed.
//
// 🟥 AND IT IS SUBTRACTED, NEVER COUNTED. A timer that ticks toward a deadline
//    does not run while a laptop is shut — which is the entire scenario. Two
//    timestamps and a subtraction give the same answer after a lid has been
//    closed for a week as after a coffee break.

import { plaintextExpired, PLAINTEXT_IDLE_MS, VAULT_LOCK_IDLE_MS } from './sessionPolicy';
import { wipePlaintext } from './wipeLocal';
import { isShared } from './sharedMachine';
import { isEnrolled, lock as lockVault, isUnlocked } from '../crypto/vault';

const KEY = 'allo_last_active_v1';
/* writing on every input would be a localStorage write per keystroke; the
   window is a day, so a minute of resolution is far finer than anything asks */
const WRITE_EVERY_MS = 60 * 1000;
let _lastWrite = 0;

/* 🟥 AFTER A WIPE, THE RE-SYNC WAITS FOR A PERSON. Re-requesting the archive the
   moment it is dropped would put it straight back on the disk of a machine
   nobody is using — the wipe undone by the thing meant to recover from it. The
   recovery is owed to somebody RETURNING, so it is armed by the wipe and fired
   by the next real input, which is the same signal the heartbeat already trusts
   to mean a person is present. */
let _needsResync = false;
let _onReturn = null;

export function markActive(now = Date.now()) {
  if (_needsResync) {
    _needsResync = false;
    try { _onReturn?.(); } catch { /* the caller's problem, not the stamp's */ }
  }
  if (now - _lastWrite < WRITE_EVERY_MS) return;
  _lastWrite = now;
  try { localStorage.setItem(KEY, String(now)); } catch { /* private mode */ }
}

export function lastActiveAt() {
  try {
    const v = Number(localStorage.getItem(KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch { return null; }
}

/**
 * Drop the archive if it has been sitting unread too long. Returns true if it did.
 *
 * 🟢 STAMPS ON THE WAY OUT, WHETHER IT WIPED OR NOT. Without that, a browser
 *    whose stamp is missing wipes on every single call — the unknown case never
 *    resolves itself, and a fresh pair would re-sync in a loop.
 */
export async function sweepIfIdle(now = Date.now()) {
  /* 🟥 A STAMP FROM THE FUTURE IS NOT A DECISION, IT IS A BROKEN CLOCK — and both
     obvious answers are wrong. Reading it as "expired" destroys the only durable
     copy of a conversation because a machine's clock is off, which happens for
     dull reasons (a dead RTC, an NTP correction, a timezone fix). Reading it as
     "just now" keeps the archive alive for ever on any machine running fast, and
     the clock is precisely what somebody at that keyboard can change.

     🟢 SO THE STAMP IS REPAIRED RATHER THAN OBEYED. Rewritten to now, nothing is
        destroyed and the window genuinely starts — a wrong clock costs one day of
        protection, not the conversation and not the feature. */
  const _seen = lastActiveAt();
  if (_seen !== null && _seen > now) {
    console.warn('[plaintext] a stamp from the future — clock skew; restarting the window from now');
    try { localStorage.setItem(KEY, String(now)); } catch { /* private mode */ }
    _lastWrite = now;
  }
  /* 🟥 AN ENROLLED BROWSER LOCKS; IT DOES NOT WIPE. Wiping exists because
     plaintext on a disk cannot be protected any other way — ciphertext can. To
     run both would throw away data that was already safe and charge the user a
     re-sync for a protection they had paid for properly. They are alternatives,
     and which one applies is decided here, once. */
  if (await isEnrolled().catch(() => false)) {
    const idleFor = now - (lastActiveAt() ?? 0);
    if (isUnlocked() && (lastActiveAt() === null || idleFor >= VAULT_LOCK_IDLE_MS)) {
      console.warn('[vault] idle — dropping the key; the caches stay and become unreadable');
      lockVault();
    }
    _lastWrite = 0;
    markActive(now);
    return false;   // 🟢 nothing was lost, so nothing has to be re-synced
  }

  const stale = plaintextExpired(lastActiveAt(), now, isShared());
  if (stale) {
    console.warn('[plaintext] idle beyond the window — dropping the local archive');
    await wipePlaintext().catch(() => {});
    _needsResync = true;
  }
  /* 🟥 STAMPED WHETHER IT WIPED OR NOT, or a browser with no stamp wipes on
     every single call: the unknown case would never resolve itself and a fresh
     pair would re-sync in a loop. Reset the throttle first — markActive is rate
     limited to a write a minute and this write is the one that matters. */
  _lastWrite = 0;
  const armed = _needsResync;
  _needsResync = false;          // markActive must not fire onReturn for OUR stamp
  markActive(now);
  _needsResync = armed;
  return stale;
}

/* 🟥 ONE CHECK PER LOAD, AWAITED BY EVERY READER — NOT A RACE WITH THEM. The
   history cache is served the instant it is asked for, so a guard that merely
   ran "at startup" would lose to it on a slow boot and paint a day-old archive
   before deleting it. Memoised here so the first reader pays for it, every
   later one gets the same settled promise, and no caller needs to know it
   was not first. */
let _once = null;
export function ensureFresh() {
  if (!_once) _once = sweepIfIdle().catch(() => false);
  return _once;
}

/** Keep checking while the tab is open — a tab left open all night is the case. */
export function startPlaintextGuard(onReturn) {
  _onReturn = typeof onReturn === 'function' ? onReturn : null;
  const beat = async () => {
    /* 🟢 the boot check owns the first sweep; this only handles time passing
       while the tab has been sitting there. */
    await ensureFresh();
    await sweepIfIdle();
  };
  const onVis = () => { if (document.visibilityState === 'visible') beat(); };
  /* 🟥 CHECKED FAR MORE OFTEN THAN THE WINDOW IS LONG. A 24-hour interval on a
     24-hour window means the wipe lands anywhere up to a day late; the check is
     cheap and the deadline should be met, not approximated. */
  const timer = setInterval(beat, 5 * 60 * 1000);
  document.addEventListener('visibilitychange', onVis);
  beat();
  return () => {
    clearInterval(timer);
    document.removeEventListener('visibilitychange', onVis);
    _onReturn = null;
  };
}

export { PLAINTEXT_IDLE_MS };
